<template>
    <div>
        <b-card no-body>
            <v-table
                ref="table"
                :items-provider="provider"
                :fields="fields"
                :buttons="buttons"
                search
                pagination="server">
                <template #additional-actions>
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-flat-secondary" v-on:click="refreshRuns">
                            <feather-icon icon="RotateCwIcon" class="mr-50" size="14"></feather-icon>
                            Yenile
                        </button>
                    </div>
                </template>
            </v-table>
        </b-card>

        <b-card no-body class="mt-1">
            <b-overlay :show="detailLoading" opacity="0.2">
                <div class="p-1 border-bottom d-flex flex-wrap justify-content-between align-items-center">
                    <div>
                        <h4 class="mb-25">Servis Çalışma Detayı</h4>
                        <small v-if="selectedRun">
                            Başlangıç: {{ selectedRun.StartedAt || '-' }} | Bitiş: {{ selectedRun.CompletedAt || '-' }}
                        </small>
                        <small v-else>Detay görüntülemek için bir çalışma seçin.</small>
                    </div>
                    <button v-if="selectedRun" class="btn btn-flat-secondary btn-sm" v-on:click="clearDetail">
                        Temizle
                    </button>
                </div>

                <div v-if="selectedRun" class="p-1">
                    <b-alert show class="mb-1" :variant="runStatusVariant(selectedRun.Status)">
                        <div class="d-flex flex-wrap align-items-center justify-content-between">
                            <div>
                                <b-badge :variant="runStatusVariant(selectedRun.Status)" class="mr-50">{{ selectedRun.Status }}</b-badge>
                                <span>{{ selectedRun.Message || 'Ek bilgi bulunmuyor.' }}</span>
                            </div>
                        </div>
                    </b-alert>

                    <div class="border rounded p-1 mb-1">
                        <div class="d-flex flex-wrap justify-content-between align-items-center">
                            <div>
                                <h5 class="mb-25">Bildirimler</h5>
                                <small class="text-muted">
                                    Ekip bildirimi gece otomatik gönderilir. Nakliyeci fiyat listeleri
                                    {{ carrierAutoSend ? 'otomatik gönderim modundadır' : 'gönderilmek üzere onayınızı bekler' }}.
                                </small>
                            </div>
                            <div class="d-flex flex-wrap">
                                <button class="btn btn-sm btn-flat-primary mr-50"
                                    :disabled="notificationLoading"
                                    v-on:click="previewTeamNotification">
                                    <feather-icon icon="MailIcon" size="14" class="mr-25"></feather-icon>
                                    Ekip Bildirimini Önizle
                                </button>
                                <button v-if="canSendCarrierNotification"
                                    class="btn btn-sm btn-primary"
                                    :disabled="notificationLoading || pendingCarrierCount === 0"
                                    v-on:click="sendCarrierNotifications([])">
                                    <feather-icon icon="SendIcon" size="14" class="mr-25"></feather-icon>
                                    Nakliyecilere Gönder ({{ pendingCarrierCount }})
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-2 col-6 mb-1">
                            <v-stats-box text="Toplam Sözleşme" icon="description" variant="primary" :value="selectedRun.AgreementCount"></v-stats-box>
                        </div>
                        <div class="col-md-2 col-6 mb-1">
                            <v-stats-box text="İşlenen" icon="fact_check" variant="info" :value="selectedRun.ProcessedAgreementCount"></v-stats-box>
                        </div>
                        <div class="col-md-2 col-6 mb-1">
                            <v-stats-box text="Güncellenen" icon="done_all" variant="success" :value="selectedRun.UpdatedAgreementCount"></v-stats-box>
                        </div>
                        <div class="col-md-2 col-6 mb-1">
                            <v-stats-box text="Atlanan" icon="skip_next" variant="warning" :value="selectedRun.SkippedAgreementCount"></v-stats-box>
                        </div>
                        <div class="col-md-2 col-6 mb-1">
                            <v-stats-box text="Hata" icon="error" variant="danger" :value="selectedRun.ErrorCount"></v-stats-box>
                        </div>
                        <div class="col-md-2 col-6 mb-1">
                            <v-stats-box text="Güncel Yakıt" icon="local_gas_station" variant="secondary" :value="selectedRun.CurrentFuelPriceStr || '-'"></v-stats-box>
                        </div>
                    </div>

                    <div class="row mt-1">
                        <div class="col-md-5 mb-1">
                            <label class="form-label">Sözleşme Loglarında Ara</label>
                            <b-input-group class="input-group-merge">
                                <template #prepend>
                                    <b-input-group-text>
                                        <feather-icon icon="SearchIcon"></feather-icon>
                                    </b-input-group-text>
                                </template>
                                <b-form-input v-model="agreementSearch" placeholder="Cari, sözleşme kodu, durum, mesaj"></b-form-input>
                            </b-input-group>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-bordered table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th class="text-nowrap">Sözleşme Kodu</th>
                                    <th class="text-nowrap">Sözleşme</th>
                                    <th class="text-nowrap">Cari</th>
                                    <th class="text-nowrap">Durum</th>
                                    <th class="text-nowrap">Son Rota Başlangıç</th>
                                    <th class="text-nowrap">Önceki Yakıt</th>
                                    <th class="text-nowrap">Güncel Yakıt</th>
                                    <th class="text-nowrap">Değişim</th>
                                    <th class="text-nowrap">Eşik</th>
                                    <th class="text-nowrap">Yakıt Etki</th>
                                    <th class="text-nowrap">İşlenen Rota</th>
                                    <th class="text-nowrap">Güncellenen Rota</th>
                                    <th class="text-nowrap">Mesaj</th>
                                    <th class="text-nowrap">Nakliyeci Bildirimi</th>
                                    <th class="text-nowrap">Detay</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="agreement in filteredAgreements">
                                    <tr :key="agreement.Id">
                                        <td class="text-nowrap">{{ agreement.AgreementCode || '-' }}</td>
                                        <td class="text-nowrap">{{ agreement.AgreementName || '-' }}</td>
                                        <td class="text-nowrap">{{ agreement.CariCode }} - {{ agreement.CariName }}</td>
                                        <td class="text-nowrap">
                                            <b-badge :variant="agreementStatusVariant(agreement.Status)">{{ agreement.Status }}</b-badge>
                                        </td>
                                        <td class="text-nowrap text-center">{{ agreement.LastRouteStartDate || '-' }}</td>
                                        <td class="text-nowrap text-right">{{ agreement.PreviousFuelPriceStr || '-' }}</td>
                                        <td class="text-nowrap text-right">{{ agreement.CurrentFuelPriceStr || '-' }}</td>
                                        <td class="text-nowrap text-right">{{ agreement.FuelChangeRateStr || '-' }}</td>
                                        <td class="text-nowrap text-right">{{ agreement.ThresholdRateStr || '-' }}</td>
                                        <td class="text-nowrap text-right">{{ agreement.FuelAffectValueStr || '-' }}</td>
                                        <td class="text-nowrap text-center">{{ agreement.EvaluatedRouteCount }}</td>
                                        <td class="text-nowrap text-center">{{ agreement.UpdatedRouteCount }}</td>
                                        <td class="text-wrap">{{ agreement.ResultMessage || '-' }}</td>
                                        <td class="text-nowrap">
                                            <template v-if="notificationOf(agreement.Id)">
                                                <b-badge :variant="carrierStatusVariant(agreement.Id)" class="mb-25">
                                                    {{ carrierStatusText(agreement.Id) }}
                                                </b-badge>
                                                <div>
                                                    <button class="btn btn-sm btn-flat-secondary px-50"
                                                        title="Nakliyeciye gidecek maili önizle"
                                                        v-on:click="previewCarrierNotification(agreement.Id)">
                                                        <feather-icon icon="EyeIcon" size="14"></feather-icon>
                                                    </button>
                                                    <button class="btn btn-sm btn-flat-secondary px-50"
                                                        title="Fiyat listesi ekini indir"
                                                        v-on:click="downloadPriceList(agreement.Id)">
                                                        <feather-icon icon="DownloadIcon" size="14"></feather-icon>
                                                    </button>
                                                    <button v-if="canSendCarrierNotification"
                                                        class="btn btn-sm btn-flat-primary px-50"
                                                        title="Bu nakliyeciye gönder"
                                                        :disabled="notificationLoading || !isCarrierPending(agreement.Id)"
                                                        v-on:click="sendCarrierNotifications([agreement.Id])">
                                                        <feather-icon icon="SendIcon" size="14"></feather-icon>
                                                    </button>
                                                </div>
                                            </template>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                        <td class="text-nowrap text-center">
                                            <button
                                                class="btn btn-sm btn-flat-primary"
                                                :disabled="(agreement.Routes || []).length === 0"
                                                v-on:click="toggleAgreementRoutes(agreement.Id)">
                                                <feather-icon :icon="isAgreementExpanded(agreement.Id) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" class="mr-25"></feather-icon>
                                                {{ (agreement.Routes || []).length }}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="isAgreementExpanded(agreement.Id)" :key="agreement.Id + '-routes'">
                                        <td colspan="1000" class="bg-light">
                                            <div class="table-responsive">
                                                <table class="table table-sm table-striped table-bordered mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th class="text-nowrap">Grup Kodu</th>
                                                            <th class="text-nowrap">Başlangıç Bölgesi</th>
                                                            <th class="text-nowrap">Bitiş Bölgesi</th>
                                                            <th class="text-nowrap">Araç Tipi</th>
                                                            <th class="text-nowrap">Stok Kodu</th>
                                                            <th class="text-nowrap">Ürün</th>
                                                            <th class="text-nowrap">Taşıma Tipi</th>
                                                            <th class="text-nowrap">Baz Tarih</th>
                                                            <th class="text-nowrap">Baz Mazot</th>
                                                            <th class="text-nowrap">Değişim</th>
                                                            <th class="text-nowrap">Eski Fiyat</th>
                                                            <th class="text-nowrap">Yeni Fiyat</th>
                                                            <th class="text-nowrap">Fark</th>
                                                            <th class="text-nowrap">Sonuç</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="route in agreement.Routes" :key="route.Id">
                                                            <td class="text-nowrap">{{ route.GroupCode || '-' }}</td>
                                                            <td class="text-nowrap">{{ route.StartRegionName || '-' }}</td>
                                                            <td class="text-nowrap">{{ route.EndRegionName || '-' }}</td>
                                                            <td class="text-nowrap">{{ route.VehicleTypeName || '-' }}</td>
                                                            <td class="text-nowrap">{{ route.StockCode || '-' }}</td>
                                                            <td class="text-nowrap">{{ route.StockName || '-' }}</td>
                                                            <td class="text-nowrap">{{ route.TransportStatus || '-' }}</td>
                                                            <td class="text-nowrap text-center">{{ route.FuelBaseDate || '-' }}</td>
                                                            <td class="text-nowrap text-right">{{ route.PreviousFuelPriceStr || '-' }}</td>
                                                            <td class="text-nowrap text-right">{{ route.FuelChangeRateStr || '-' }}</td>
                                                            <td class="text-nowrap text-right">{{ route.OldPriceStr || '-' }}</td>
                                                            <td class="text-nowrap text-right">{{ route.NewPriceStr || '-' }}</td>
                                                            <td class="text-nowrap text-right">{{ route.PriceDeltaStr || '-' }}</td>
                                                            <td class="text-wrap">
                                                                <b-badge :variant="route.IsUpdated ? 'light-success' : 'light-secondary'">
                                                                    {{ route.IsUpdated ? 'Güncellendi' : 'Atlandı' }}
                                                                </b-badge>
                                                                <small v-if="route.SkipReason" class="d-block text-muted">{{ route.SkipReason }}</small>
                                                            </td>
                                                        </tr>
                                                        <tr v-if="(agreement.Routes || []).length === 0">
                                                            <td colspan="1000" class="text-center">İlgili çalışmada rota detayı oluşmadı.</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr v-if="filteredAgreements.length === 0">
                                    <td colspan="1000" class="text-center">Seçili çalışmaya ait kayıt bulunamadı.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </b-overlay>
        </b-card>

        <b-modal
            v-model="previewVisible"
            :title="previewTitle"
            size="lg"
            ok-only
            ok-title="Kapat"
            scrollable>
            <b-overlay :show="previewLoading" opacity="0.2">
                <!-- İçerik backend'de üretilen mail gövdesidir; ekranda birebir aynısı gösterilir
                     ki gönderilecek mail ile önizleme arasında fark oluşmasın. -->
                <div class="border rounded p-1 bg-white" v-html="previewContent"></div>
            </b-overlay>
        </b-modal>
    </div>
</template>

<script>
import { AgreementApi } from '@/api/agreement'
import VStatsBox from '@/components/VStatsBox.vue'
import Auth from '@/utilities/auth'
import Additional from '@/utilities/additional'
import { BAlert, BBadge, BCard, BFormInput, BInputGroup, BInputGroupText, BModal, BOverlay } from 'bootstrap-vue'

export default {
    components:{
        BAlert,
        BBadge,
        BCard,
        BFormInput,
        BInputGroup,
        BInputGroupText,
        BModal,
        BOverlay,
        VStatsBox
    },
    data(){
        return {
            detailLoading:false,
            agreementSearch:'',
            selectedRun:null,
            expandedAgreementIds:{},
            notificationSummary:null,
            notificationLoading:false,
            previewVisible:false,
            previewLoading:false,
            previewTitle:'',
            previewContent:'',
            fields:[
                {name:'Çalışma Başlangıcı',key:'StartedAt',sortable:true,thClass:'w-15'},
                {name:'Çalışma Bitişi',key:'CompletedAt',sortable:true,thClass:'w-15'},
                {name:'Durum',key:'Status',sortable:true,thClass:'w-10'},
                {name:'Güncel Yakıt',key:'CurrentFuelPriceStr',sortKey:'CurrentFuelPrice',sortable:true,thClass:'w-10',tdClass:'text-right'},
                {name:'Toplam Sözleşme',key:'AgreementCount',sortable:true,thClass:'w-10',tdClass:'text-center'},
                {name:'İşlenen',key:'ProcessedAgreementCount',sortable:true,thClass:'w-10',tdClass:'text-center'},
                {name:'Güncellenen',key:'UpdatedAgreementCount',sortable:true,thClass:'w-10',tdClass:'text-center'},
                {name:'Atlanan',key:'SkippedAgreementCount',sortable:true,thClass:'w-10',tdClass:'text-center'},
                {name:'Rota Güncelleme',key:'UpdatedRouteCount',sortable:true,thClass:'w-10',tdClass:'text-center'},
                {name:'Hata',key:'ErrorCount',sortable:true,thClass:'w-10',tdClass:'text-center'},
            ],
            buttons:[
                { text:'Detay', key:'detail', icon:'EyeIcon', variant:'primary', action:this.showDetail }
            ]
        }
    },
    mounted(){
        this.loadLatestRun()
    },
    methods:{
        provider(data){
            return AgreementApi.DailyRouteCheckRuns(data)
        },
        refreshRuns(){
            this.$refs.table.refresh()
            if(this.selectedRun){
                this.loadRunDetail(this.selectedRun.Id)
            }else{
                this.loadLatestRun()
            }
        },
        loadLatestRun(){
            AgreementApi.DailyRouteCheckRuns({
                Skip:1,
                Take:1,
                QueryParam:'',
                IsAscending:false,
                OrderColumnName:'StartedAt'
            }).then(res => {
                const run = (((res || {}).data || {}).Data || {}).Data?.[0]
                if(run){
                    this.loadRunDetail(run.Id)
                }
            }).catch(() => {})
        },
        showDetail(item){
            this.loadRunDetail(item.Id)
        },
        loadRunDetail(id){
            this.detailLoading = true
            this.agreementSearch = ''
            this.expandedAgreementIds = {}
            AgreementApi.DailyRouteCheckRunDetail(id).then(res => {
                if(res.data.Success){
                    this.selectedRun = res.data.Data
                    this.loadNotificationSummary(id)
                }else{
                    this.$result(res)
                }
                this.detailLoading = false
            }).catch(err => {
                this.$result(null,err)
                this.detailLoading = false
            })
        },
        // Bildirim durumu ayrı uçtan gelir; çalışma detayı ile aynı ekranda birleştirilir.
        loadNotificationSummary(runId){
            this.notificationSummary = null
            AgreementApi.FuelUpdateNotificationSummary(runId).then(res => {
                if(res.data.Success){
                    this.notificationSummary = res.data.Data
                }
            }).catch(() => {})
        },
        notificationOf(agreementLogId){
            return (this.$safe(this.notificationSummary,'UpdatedAgreements') || [])
                .find(x => x.AgreementLogId === agreementLogId) || null
        },
        isCarrierPending(agreementLogId){
            const notification = this.notificationOf(agreementLogId)
            // Gönderime engel bir sebep varsa (süresi dolmuş sözleşme, arada yeni fiyat
            // değişimi, adres eksikliği) düğme açılmaz. Sebebi backend belirler.
            return !!notification && !notification.CarrierNotificationSentAtStr && !notification.BlockReason
        },
        carrierStatusText(agreementLogId){
            const notification = this.notificationOf(agreementLogId)
            if(!notification) return '-'
            if(notification.CarrierNotificationSentAtStr) return 'Gönderildi ' + notification.CarrierNotificationSentAtStr
            if(notification.CarrierNotificationError) return notification.CarrierNotificationError
            if(notification.BlockReason) return notification.BlockReason
            return 'Bekliyor'
        },
        carrierStatusVariant(agreementLogId){
            const notification = this.notificationOf(agreementLogId)
            if(!notification) return 'light-secondary'
            if(notification.CarrierNotificationSentAtStr) return 'light-success'
            if(notification.CarrierNotificationError || notification.BlockReason) return 'light-danger'
            return 'light-warning'
        },
        previewTeamNotification(){
            this.previewTitle = 'Ekip Bildirimi Önizleme'
            this.previewContent = ''
            this.previewVisible = true
            this.previewLoading = true
            AgreementApi.PreviewTeamFuelUpdateNotification(this.selectedRun.Id).then(res => {
                if(res.data.Success) this.previewContent = res.data.Data
                else this.$result(res)
                this.previewLoading = false
            }).catch(err => {
                this.$result(null,err)
                this.previewLoading = false
            })
        },
        previewCarrierNotification(agreementLogId){
            const notification = this.notificationOf(agreementLogId)
            this.previewTitle = 'Nakliyeci Bildirimi Önizleme - ' + (notification ? notification.CariName : '')
            this.previewContent = ''
            this.previewVisible = true
            this.previewLoading = true
            AgreementApi.PreviewCarrierFuelUpdateNotification(agreementLogId).then(res => {
                if(res.data.Success) this.previewContent = res.data.Data
                else this.$result(res)
                this.previewLoading = false
            }).catch(err => {
                this.$result(null,err)
                this.previewLoading = false
            })
        },
        sendCarrierNotifications(agreementLogIds){
            const count = agreementLogIds.length > 0 ? agreementLogIds.length : this.pendingCarrierCount
            this.$confirm(
                'Nakliyecilere bildirim gönderilecek',
                `${count} nakliyeciye yeni fiyat listesi maili gönderilecektir. Mail sistem dışına çıkar ve geri alınamaz. Onaylıyor musunuz?`,
                'Gönder'
            ).then(confirmed => {
                if(!confirmed) return

                this.notificationLoading = true
                AgreementApi.SendCarrierFuelUpdateNotifications({
                    RunId:this.selectedRun.Id,
                    AgreementLogIds:agreementLogIds
                }).then(res => {
                    this.$result(res)
                    this.loadNotificationSummary(this.selectedRun.Id)
                    this.notificationLoading = false
                }).catch(err => {
                    this.$result(null,err)
                    this.notificationLoading = false
                })
            })
        },
        downloadPriceList(agreementLogId){
            const notification = this.notificationOf(agreementLogId)
            AgreementApi.DownloadCarrierPriceList(agreementLogId).then(res => {
                const name = `${notification ? notification.AgreementCode : 'Sozlesme'} - Yeni Fiyat Listesi.xlsx`
                Additional.DownloadFile(res.data,name)
            }).catch(err => this.$result(null,err))
        },
        clearDetail(){
            this.selectedRun = null
            this.agreementSearch = ''
            this.expandedAgreementIds = {}
            this.notificationSummary = null
        },
        toggleAgreementRoutes(id){
            this.$set(this.expandedAgreementIds,id,!this.expandedAgreementIds[id])
        },
        isAgreementExpanded(id){
            return !!this.expandedAgreementIds[id]
        },
        runStatusVariant(status){
            if(status === 'Tamamlandı') return 'success'
            if(status === 'Hatalı Tamamlandı') return 'warning'
            if(status === 'Başarısız') return 'danger'
            return 'secondary'
        },
        agreementStatusVariant(status){
            if(status === 'Güncellendi') return 'success'
            if(status === 'Hata') return 'danger'
            return 'secondary'
        }
    },
    computed:{
        canSendCarrierNotification(){
            return Auth.hasRole(['agreement.fuel.carrier.notification'])
        },
        carrierAutoSend(){
            return this.$safe(this.notificationSummary,'CarrierAutoSend') || false
        },
        pendingCarrierCount(){
            return (this.$safe(this.notificationSummary,'UpdatedAgreements') || [])
                .filter(x => !x.CarrierNotificationSentAtStr && !x.BlockReason).length
        },
        filteredAgreements(){
            const agreements = this.$safe(this.selectedRun,'Agreements') || []
            const query = (this.agreementSearch || '').trim().toLowerCase()
            if(query.length === 0){
                return agreements
            }

            return agreements.filter(agreement => {
                return [
                    agreement.AgreementCode,
                    agreement.AgreementName,
                    agreement.CariCode,
                    agreement.CariName,
                    agreement.Status,
                    agreement.ResultMessage
                ].some(value => value != null && value.toString().toLowerCase().includes(query))
            })
        }
    }
}
</script>
